/**
 * Insight Renderer - For Strategy / Analysis Agents
 * Used when: output.sections[] exists
 * 
 * Features:
 * - Executive Summary highlighted at top
 * - Sections as collapsible cards (only for long sections)
 * - Section icons based on semantic keywords
 * - Recommended Actions panel if detected
 */

import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

import {
  ArrowRight,
  CaretDown,
  CaretUp,
  ChartBar,
  CheckCircle,
  Clock,
  CurrencyDollar,
  FileText,
  Lightbulb,
  Lightning,
  MagnifyingGlass,
  Shield,
  Target,
  TrendUp,
  UsersThree,
  Warning
} from '@phosphor-icons/react';
// Custom components for ReactMarkdown to prevent unwanted elements
const markdownComponents = {
  // Render hr as a subtle divider instead of a line through content
  hr: () => <div className="my-4 border-t border-[rgba(255,255,255,0.08)]" />,
  // Ensure paragraphs don't get extra styling
  p: ({ children }) => <p className="my-2">{children}</p>,
};

// Section icon mapping based on semantic keywords
const getSectionIcon = (label) => {
  const lower = (label || '').toLowerCase();
  if (lower.includes('summary') || lower.includes('overview') || lower.includes('executive')) return Target;
  if (lower.includes('analysis') || lower.includes('finding')) return ChartBar;
  if (lower.includes('risk')) return Warning;
  if (lower.includes('cost') || lower.includes('financial') || lower.includes('budget')) return CurrencyDollar;
  if (lower.includes('time') || lower.includes('timeline') || lower.includes('schedule')) return Clock;
  if (lower.includes('compliance') || lower.includes('security')) return Shield;
  if (lower.includes('recommendation') || lower.includes('action') || lower.includes('next')) return Lightbulb;
  if (lower.includes('impact') || lower.includes('benefit')) return TrendUp;
  if (lower.includes('team') || lower.includes('resource') || lower.includes('people')) return UsersThree;
  return FileText;
};

// Detect if section is summary-type
const isSummarySection = (key) => {
  const lower = (key || '').toLowerCase();
  return lower.includes('summary') || lower.includes('overview') || lower.includes('executive');
};

// Detect if section is recommendation-type
const isRecommendationSection = (key) => {
  const lower = (key || '').toLowerCase();
  return lower.includes('recommendation') || lower.includes('action') || lower.includes('next step');
};

// Check if content is long enough to warrant collapsing
const isLongContent = (content) => {
  if (!content) return false;
  return content.length > 500 || content.split('\n').length > 8;
};

export const InsightRenderer = ({ content, onAction }) => {
  const sections = content?.sections || [];
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // Categorize sections
  const { summarySection, recommendationSections, otherSections } = useMemo(() => {
    let summary = null;
    const recommendations = [];
    const others = [];
    
    sections.forEach(section => {
      const key = section.key || section.label || '';
      if (isSummarySection(key) && !summary) {
        summary = section;
      } else if (isRecommendationSection(key)) {
        recommendations.push(section);
      } else {
        others.push(section);
      }
    });
    
    return { summarySection: summary, recommendationSections: recommendations, otherSections: others };
  }, [sections]);
  
  const toggleSection = (key) => {
    setCollapsedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Render Executive Summary (highlighted)
  const renderSummary = () => {
    if (!summarySection) return null;
    
    return (
      <div className="mb-6 p-5 bg-gradient-to-r from-[#6366F1]/10 to-[#8B5CF6]/10 border border-[#6366F1]/30 rounded-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 bg-[#6366F1]/20 rounded-lg">
            <Target size={16} className="text-[#6366F1]" />
          </div>
          <h3 className="text-sm font-semibold text-[#6366F1] uppercase tracking-wide">
            Executive Summary
          </h3>
        </div>
        <div className="prose prose-invert prose-sm max-w-none text-gray-200">
          <ReactMarkdown components={markdownComponents}>{summarySection.content}</ReactMarkdown>
        </div>
      </div>
    );
  };
  
  // Render a single section card
  const renderSectionCard = (section, idx) => {
    const key = section.key || `section-${idx}`;
    const label = section.label || section.key || `Section ${idx + 1}`;
    const sectionContent = section.content || '';
    // All sections are collapsible and collapsed by default
    const isCollapsed = collapsedSections[key] !== false; // Default to collapsed (true)
    const Icon = getSectionIcon(label);
    
    return (
      <div 
        key={key}
        className="bg-[#111111] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden transition-all duration-200 hover:border-[rgba(255,255,255,0.15)]"
      >
        {/* Header - always clickable */}
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-[rgba(255,255,255,0.02)]"
          onClick={() => toggleSection(key)}
        >
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-[rgba(255,255,255,0.05)] rounded-lg">
              <Icon size={14} className="text-gray-400" />
            </div>
            <span className="text-sm font-medium text-white">{label}</span>
          </div>
          {isCollapsed ? (
            <CaretDown size={16} className="text-gray-500" />
          ) : (
            <CaretUp size={16} className="text-gray-500" />
          )}
        </div>
        
        {/* Content - only shown when expanded */}
        {!isCollapsed && (
          <div className="px-4 pb-4 border-t border-[rgba(255,255,255,0.05)]">
            <div className="pt-4 text-sm text-gray-300 leading-relaxed prose prose-invert prose-sm max-w-none">
              <ReactMarkdown components={markdownComponents}>{sectionContent}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render Recommendations Panel
  const renderRecommendations = () => {
    if (recommendationSections.length === 0) return null;
    
    // Extract action items from recommendations
    const actionItems = [];
    recommendationSections.forEach(rec => {
      const recContent = rec.content || '';
      // Parse bullet points
      const bullets = recContent.match(/^[\s]*[-•*\d.]+[\s]+(.+)$/gm) || [];
      bullets.forEach(item => {
        const cleanText = item.replace(/^[\s]*[-•*\d.]+[\s]+/, '').trim();
        if (cleanText && cleanText.length > 10) {
          actionItems.push(cleanText);
        }
      });
    });
    
    return (
      <div className="mt-6 p-5 bg-[#0D0D0D] border border-[rgba(255,255,255,0.08)] rounded-xl">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 bg-[#14F195]/10 rounded-lg">
            <Lightbulb size={16} className="text-[#14F195]" />
          </div>
          <h3 className="text-sm font-semibold text-white">Recommended Actions</h3>
        </div>
        
        {/* Render recommendation sections */}
        <div className="space-y-4">
          {recommendationSections.map((rec, idx) => (
            <div key={idx} className="prose prose-invert prose-sm max-w-none text-gray-300">
              <ReactMarkdown components={markdownComponents}>{rec.content}</ReactMarkdown>
            </div>
          ))}
        </div>
        
        {/* Quick Action Buttons */}
        {actionItems.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[rgba(255,255,255,0.05)]">
            <button 
              onClick={() => onAction?.('export')}
              className="px-3 py-1.5 text-xs bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-1.5"
            >
              <ArrowRight size={12} />
              Export Report
            </button>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-4">
      {/* Executive Summary - always at top */}
      {renderSummary()}
      
      {/* Other sections as cards */}
      {otherSections.length > 0 && (
        <div className="space-y-3">
          {otherSections.map((section, idx) => renderSectionCard(section, idx))}
        </div>
      )}
      
      {/* Recommendations panel */}
      {renderRecommendations()}
      
      {/* Ready for review message */}
      <p className="text-xs text-gray-500 text-center pt-2">
        This analysis is ready for review and action.
      </p>
    </div>
  );
};

export default InsightRenderer;
